import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Menu,
    Card,
    CardContent,
    CardHeader,
    InputAdornment,
    Alert,
    LinearProgress,
    CircularProgress,
    Tooltip,
    Badge,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemIcon
} from '@mui/material';
import {
    Search,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    X,
    MeetingRoom,
    Hotel,
    Bed,
    Groups,
    AttachMoney,
    CalendarToday,
    Person,
    Phone,
    Email,
    CheckCircle,
    Warning,
    Error,
    Info,
    KingBed,
    Bathtub,
    Wifi,
    Restaurant,
    LocalParking,
    FitnessCenter,
    Pool,
    Air,
    Tv,
    AcUnit,
    Kitchen,
    BusinessCenter,
    DirectionsCar,
    AccessTime,
    NightsStay,
    Star,
    SquareFoot,
    Numbers,
    EventAvailable,
    EventBusy,
    Schedule,
    RoomService,
    CleaningServices,
    Build,
    Lock,
    LockOpen,
    Visibility,
    ArrowForward,
    DoubleArrow,
    TrendingUp,
    TrendingDown,
    Equalizer,
    PieChart,
    BarChart,
    Timeline,
    Inventory2,
    Store,
    CheckCircleOutline,
    Cancel,
    Pending,
    LocalHotel,
    BedroomParent,
    BedroomChild,
    SingleBed,
    KingBedOutlined,
    QueenBed,
    BedOutlined
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    APIBookingList,
    APIRoomNames,
    APIBrancheNamesFilter,
    APIBookingTypeNames
} from '../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../services/Text';
import {
    formatCreatedAtDate,
    formatNumberWithComma,
    getCategoriesNameById,
    getUserDetails,
    recordsPerPage,
    formatCurrency
} from '../../utils/helpers';
import {
    exportRoomStatusToCSV,
    exportRoomStatusToExcel,
    exportRoomStatusToPDF,
    checkRoomAvailability,
    getRoomStatistics
} from '../../services/RoomStatusService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utils/Permission';
import useCustomToast from '../../hooks/useToast';

const RoomStatus = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();

    // State management
    const [pageState, setPageState] = useState({
        pageData: [],
        rooms: [],
        bookings: [],
        stats: {
            total_rooms: 0,
            available_rooms: 0,
            booked_rooms: 0,
            checked_in_rooms: 0,
            maintenance_rooms: 0,
            occupancy_rate: 0,
            revenue_potential: 0,
            average_rate: 0
        }
    });
    
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Data states
    const [rooms, setRooms] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [branchNamesFilterData, setBranchNamesFilterData] = useState([]);
    const [bookingTypeData, setBookingTypeData] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState('');
    const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);

    // Filters state
    const [filters, setFilters] = useState({
        room_type: '',
        status: '',
        dateFrom: '',
        dateTo: '',
        capacity: '',
        bed_type: ''
    });

    // Dialog states
    const [filterOptions, setFilterOptions] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openBookingView, setOpenBookingView] = useState(false);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);

    // Permissions
    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        setUserRole(getUserRole());
        fetchData();
        fetchBranchNames();
    }, []);

    // Fetch data functions
    const fetchBranchNames = async () => {
        try {
            const response = await axios.get(APIBrancheNamesFilter);
            if (response.data.status === 'success') {
                setBranchNamesFilterData(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching branch names:", error);
            showToast("Error", "Failed to load branch names", "error");
        }
    };


    const fetchData = async (branchId = null) => {
        setLoading(true);
        setError(null);

        try {
            let branchIdToUse = branchId;
            if (!hasPermission(PERMISSIONS.BRANCHES)) {
                branchIdToUse = getUserDetails("branchId");
            }

            const params = {};
            if (branchIdToUse) {
                params.branch_id = branchIdToUse;
            }

            // Fetch rooms and bookings in parallel
            const [roomsRes, bookingsRes] = await Promise.all([
                axios.get(APIRoomNames, { params }),
                axios.get(APIBookingList, { params })
            ]);

            const roomsData = Array.isArray(roomsRes.data.data) ? roomsRes.data.data : [];
            const bookingsData = Array.isArray(bookingsRes.data.data) ? bookingsRes.data.data : [];

            // Calculate statistics
            const stats = getRoomStatistics(roomsData, bookingsData, filters);

            setPageState(prev => ({
                ...prev,
                rooms: roomsData,
                bookings: bookingsData,
                stats: stats
            }));

            setRooms(roomsData);
            setBookings(bookingsData);

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            console.log(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleBranchSelect = (branchId) => {
        setSelectedBranch(branchId);
        setBranchSelectionOpen(false);
        fetchData(branchId);
    };

    // Permission check
    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    // Get room status
    const getRoomStatus = (room) => {
        const roomBookings = bookings.filter(booking => 
            parseInt(booking.room_type) === parseInt(room.id)
        );

        const activeBooking = roomBookings.find(booking => 
            booking.status === 1 || booking.status === 2
        );

        if (activeBooking) {
            return {
                status: activeBooking.status === 2 ? 'checked_in' : 'booked',
                booking: activeBooking,
                customer: activeBooking.customer_name,
                checkIn: activeBooking.check_in,
                checkOut: activeBooking.check_out,
                nights: calculateNights(activeBooking.check_in, activeBooking.check_out)
            };
        }

        return {
            status: 'available',
            booking: null,
            customer: null,
            checkIn: null,
            checkOut: null,
            nights: 0
        };
    };

    // Calculate nights
    const calculateNights = (checkIn, checkOut) => {
        if (!checkIn || !checkOut) return 0;
        const start = new Date(checkIn);
        const end = new Date(checkOut);
        const diffTime = Math.abs(end - start);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    // Filter data
    const filteredData = rooms.filter(room => {
        const matchesSearch = searchTerm === '' ||
            (room.roomtype && room.roomtype.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesRoomType = !filters.room_type ||
            (room.id && room.id.toString() === filters.room_type.toString());

        const matchesCapacity = !filters.capacity ||
            (room.capacity && room.capacity >= parseInt(filters.capacity));

        const matchesBedType = !filters.bed_type ||
            (room.bedstype && room.bedstype.toString() === filters.bed_type.toString());

        // Date range filter - check availability
        let matchesDate = true;
        if (filters.dateFrom && filters.dateTo) {
            const isAvailable = checkRoomAvailability(room.id, bookings, filters.dateFrom, filters.dateTo);
            if (filters.status === 'available') {
                matchesDate = isAvailable;
            } else if (filters.status === 'booked') {
                matchesDate = !isAvailable;
            }
        }

        return matchesSearch && matchesRoomType && matchesCapacity && matchesBedType && matchesDate;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handleOpenView = (room) => {
        setSelectedRoom(room);
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
        setSelectedRoom(null);
    };

    const handleOpenBookingView = (room) => {
        const roomStatus = getRoomStatus(room);
        if (roomStatus.booking) {
            setSelectedBooking(roomStatus.booking);
            setOpenBookingView(true);
        }
    };

    const handleCloseBookingView = () => {
        setOpenBookingView(false);
        setSelectedBooking(null);
    };

    // Form handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = tomorrow.toISOString().split('T')[0];
        
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: tomorrowStr
        });
        setFilterOptions(false);
    };

    const clearFilters = () => {
        setFilters({
            room_type: '',
            status: '',
            dateFrom: '',
            dateTo: '',
            capacity: '',
            bed_type: ''
        });
        setSearchTerm('');
        setFilterOptions(false);
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handleExportExcel = async () => {
        try {
            handleExportClose();
            await exportRoomStatusToExcel(rooms, bookings, bookingTypeData, filters);
            showToast("Success", "Excel file downloaded successfully", "success");
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportPDF = async () => {
        try {
            handleExportClose();
            await exportRoomStatusToPDF(rooms, bookings, bookingTypeData, filters);
            showToast("Success", "PDF file downloaded successfully", "success");
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportCSV = async () => {
        try {
            handleExportClose();
            await exportRoomStatusToCSV(rooms, bookings);
            showToast("Success", "CSV file downloaded successfully", "success");
        } catch (error) {
            console.error('CSV export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    // Get status chip
    const getStatusChip = (room) => {
        const roomStatus = getRoomStatus(room);
        
        switch (roomStatus.status) {
            case 'available':
                return (
                    <Chip
                        icon={<CheckCircle />}
                        label="Available"
                        color="success"
                        variant="filled"
                        size="small"
                    />
                );
            case 'booked':
                return (
                    <Chip
                        icon={<Warning />}
                        label="Booked"
                        color="warning"
                        variant="filled"
                        size="small"
                    />
                );
            case 'checked_in':
                return (
                    <Chip
                        icon={<Error />}
                        label="Checked In"
                        color="error"
                        variant="filled"
                        size="small"
                    />
                );
            default:
                return (
                    <Chip
                        label="Unknown"
                        color="default"
                        variant="outlined"
                        size="small"
                    />
                );
        }
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading room status...</Typography>
        </Box>
    );
    
    if (error) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <Alert severity="error">Error: {error}</Alert>
        </Box>
    );

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
                mb: { xs: 1, sm: 0 }
            }}>
                Room Status Management ({formatNumberWithComma(pageState.stats.total_rooms || 0)})
            </Typography>
            
            {/* Display selected branch name */}
            {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
                <Box sx={{ mt: 1, mb: 2 }}>
                    <Chip
                        label={`Selected Branch: ${getCategoriesNameById(selectedBranch, branchNamesFilterData)}`}
                        color="primary"
                        variant="filled"
                        onDelete={() => {
                            setSelectedBranch('');
                            fetchData();
                        }}
                        deleteIcon={<CloseIcon />}
                    />
                </Box>
            )}
            
            {/* Room Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Total Rooms Card */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: 'primary.main',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Hotel sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Total Rooms
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {formatNumberWithComma(pageState.stats.total_rooms || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                All rooms in system
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Available Rooms Card */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: 'success.main',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CheckCircle sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Available
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {formatNumberWithComma(pageState.stats.available_rooms || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ mt: 1 }}>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={pageState.stats.total_rooms > 0 ? (pageState.stats.available_rooms / pageState.stats.total_rooms) * 100 : 0}
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Booked Rooms Card */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: 'warning.main',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Bed sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Booked
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {formatNumberWithComma(pageState.stats.booked_rooms || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ mt: 1 }}>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={pageState.stats.occupancy_rate || 0}
                                    sx={{ height: 6, borderRadius: 3, bgcolor: 'warning.dark' }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Checked In Card */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: 'error.main',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Groups sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Checked In
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {formatNumberWithComma(pageState.stats.checked_in_rooms || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Currently occupied
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Occupancy Rate Card */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: 'info.main',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <TrendingUp sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Occupancy
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {pageState.stats.occupancy_rate || 0}%
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Room utilization
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Revenue Potential Card */}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        bgcolor: 'secondary.main',
                        color: 'white',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <AttachMoney sx={{ fontSize: 30, mr: 2 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Revenue
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(pageState.stats.revenue_potential || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Daily potential
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    flexWrap: 'wrap' 
                }}>
                    {/* Buttons */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexShrink: 0,
                        order: { xs: 2, sm: 1 }
                    }}>
                        {hasPermission(PERMISSIONS.BRANCHES) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<FilterIcon />}
                                onClick={() => setBranchSelectionOpen(true)}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                                size="small"
                            >
                                Select Branch
                            </Button>
                        )}
                        
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<FilterIcon />}
                            onClick={() => setFilterOptions(true)}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                            size="small"
                        >
                            Filter Options
                        </Button>
                        
                        {/* {hasPermission(PERMISSIONS.EXPORT) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                                size="small"
                            >
                                Export Data
                            </Button>
                        )} */}
                        
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={fetchData}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                            size="small"
                        >
                            Refresh
                        </Button>
                    </Box>
                    
                    {/* Search Bar */}
                    <Paper sx={{
                        width: { xs: '100%', sm: '300px' },
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: { xs: 0, sm: 'auto' },
                        order: { xs: 1, sm: 2 },
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search Rooms..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>
                
                {/* Active Filters */}
                {(filters.room_type || filters.dateFrom || filters.dateTo || filters.capacity || filters.bed_type) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.room_type && (
                            <Chip
                                label={`Room: ${getCategoriesNameById(filters.room_type, rooms)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, room_type: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.capacity && (
                            <Chip
                                label={`Min Capacity: ${filters.capacity}`}
                                onDelete={() => setFilters(prev => ({ ...prev, capacity: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.bed_type && (
                            <Chip
                                label={`Bed Type: ${getCategoriesNameById(filters.bed_type, bookingTypeData)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, bed_type: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon fontSize="small" />}
                            sx={{ minWidth: 'auto' }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>
            
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Room</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Capacity</TableCell>
                                    <TableCell>Rate</TableCell>
                                    <TableCell>Size</TableCell>
                                    <TableCell>Beds</TableCell>
                                    <TableCell>Current Guest</TableCell>
                                    <TableCell>Check In</TableCell>
                                    <TableCell>Check Out</TableCell>
                                    <TableCell>Nights</TableCell>
                                    {hasPermission(PERMISSIONS.BRANCHES) && <TableCell>Branch</TableCell>}
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((room, index) => {
                                    const roomStatus = getRoomStatus(room);
                                    // const bedType = getCategoriesNameById(room.bedstype, bookingTypeData);
                                    return (
                                        <TableRow
                                            key={room.id}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                },
                                                ...(roomStatus.status === 'available' && {
                                                    backgroundColor: 'rgba(76, 175, 80, 0.05)'
                                                }),
                                                ...(roomStatus.status === 'booked' && {
                                                    backgroundColor: 'rgba(255, 193, 7, 0.05)'
                                                }),
                                                ...(roomStatus.status === 'checked_in' && {
                                                    backgroundColor: 'rgba(244, 67, 54, 0.05)'
                                                })
                                            }}
                                        >

                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'medium' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <MeetingRoom color="primary" />
                                                    {room.roomtype}
                                                    {room.exbedcapability === 1 && (
                                                        <Tooltip title="Extra bed capability">
                                                            <Bed color="secondary" fontSize="small" />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                {/* <Typography variant="caption" display="block" color="text.secondary">
                                                    {bedType || 'N/A'}
                                                </Typography> */}
                                            </TableCell>
                                            <TableCell>
                                                {getStatusChip(room)}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Groups fontSize="small" color="action" />
                                                    {room.capacity}
                                                    {room.exbedcapability === 1 && (
                                                        <Typography variant="caption" color="secondary">
                                                            +1
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={formatCurrency(room.defaultrate || 0)}
                                                    color="success"
                                                    size="small"
                                                    icon={<AttachMoney fontSize="small" />}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <SquareFoot fontSize="small" color="action" />
                                                    {room.roomsize} {room.size_unit || 'sq ft'}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <KingBedOutlined fontSize="small" color="action" />
                                                    {room.bedsno}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {roomStatus.customer ? (
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {roomStatus.customer}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {roomStatus.status === 'checked_in' ? 'Currently staying' : 'Future booking'}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Available
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {roomStatus.checkIn ? (
                                                    <Chip
                                                        label={formatDate(roomStatus.checkIn)}
                                                        size="small"
                                                        color={roomStatus.status === 'checked_in' ? 'error' : 'warning'}
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        N/A
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {roomStatus.checkOut ? (
                                                    <Chip
                                                        label={formatDate(roomStatus.checkOut)}
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        N/A
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {roomStatus.nights > 0 ? (
                                                    <Chip
                                                        label={`${roomStatus.nights} night${roomStatus.nights > 1 ? 's' : ''}`}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        N/A
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            {hasPermission(PERMISSIONS.BRANCHES) && (
                                                <TableCell>
                                                    <Chip
                                                        label={getCategoriesNameById(room.branch_id, branchNamesFilterData)}
                                                        color="info"
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell align="center">
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleOpenView(room)}
                                                    color="primary"
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.info.light
                                                        }
                                                    }}
                                                >
                                                    <Visibility />
                                                </IconButton>
                                                
                                                {roomStatus.booking && (
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenBookingView(room)}
                                                        color="secondary"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.success.light
                                                            }
                                                        }}
                                                    >
                                                        <CalendarToday />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {/* Pagination */}
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />
                    
                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <Search sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No rooms found</Typography>
                            <Typography variant="body2">Try adjusting your search query or select a branch</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>
            
            {/* Branch Selection Dialog */}
            <Dialog
                open={branchSelectionOpen}
                onClose={() => setBranchSelectionOpen(false)}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel id="branch-select-label">Select Branch</InputLabel>
                            <Select
                                labelId="branch-select-label"
                                value={selectedBranch}
                                label="Select Branch"
                                onChange={(e) => handleBranchSelect(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Select a branch</em>
                                </MenuItem>
                                {branchNamesFilterData.map((branch) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name} ({branch.code})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
                            <strong>Note:</strong> Selecting a branch will filter rooms only from that branch. 
                            Leave unselected to view all rooms.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={() => setBranchSelectionOpen(false)}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (selectedBranch) {
                                fetchData(selectedBranch);
                            }
                            setBranchSelectionOpen(false);
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
                <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="info" />
                    Export to CSV (.csv)
                </MenuItem>
            </Menu>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                onClose={() => setFilterOptions(false)}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Rooms
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Check Today's Availability
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Room Type
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Room Type</InputLabel>
                            <Select
                                name="room_type"
                                value={filters.room_type}
                                onChange={handleFilterChange}
                                label="Room Type"
                            >
                                <MenuItem value="">All Rooms</MenuItem>
                                {rooms.map((room) => (
                                    <MenuItem key={room.id} value={room.id}>
                                        {room.roomtype}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Capacity
                        </Typography>

                        <TextField
                            fullWidth
                            label="Minimum Capacity"
                            type="number"
                            name="capacity"
                            value={filters.capacity}
                            onChange={handleFilterChange}
                            inputProps={{ min: 1 }}
                        />


                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Status
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="available">Available</MenuItem>
                                <MenuItem value="booked">Booked</MenuItem>
                                <MenuItem value="checked_in">Checked In</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={() => setFilterOptions(false)}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={() => setFilterOptions(false)}
                        color="primary"
                        variant="contained"
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Room Details Dialog */}
            <Dialog
                open={openView}
                onClose={handleCloseView}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Room Details</b></DialogTitle>
                <DialogContent>
                    {selectedRoom && (
                        <Box sx={{ p: 2 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography variant="h5" color="primary">
                                            {selectedRoom.roomtype}
                                        </Typography>
                                        {getStatusChip(selectedRoom)}
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                Room Specifications
                                            </Typography>
                                            <List dense>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <Groups color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Capacity" 
                                                        secondary={`${selectedRoom.capacity} person(s)` + (selectedRoom.exbedcapability === 1 ? ' (Extra bed available)' : '')}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <SquareFoot color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Size" 
                                                        secondary={`${selectedRoom.roomsize} ${selectedRoom.size_unit || 'sq ft'}`}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <KingBedOutlined color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Beds" 
                                                        secondary={`${selectedRoom.bedsno} bed(s) - ${getCategoriesNameById(selectedRoom.bedstype, bookingTypeData) || 'N/A'}`}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <Star color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Rating" 
                                                        secondary={selectedRoom.number_of_star ? `${selectedRoom.number_of_star} stars` : 'Not rated'}
                                                    />
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                Pricing Details
                                            </Typography>
                                            <List dense>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <AttachMoney color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Default Rate" 
                                                        secondary={formatCurrency(selectedRoom.defaultrate)}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <Bed color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Bed Charge" 
                                                        secondary={formatCurrency(selectedRoom.bedcharge)}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <Person color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Person Charge" 
                                                        secondary={formatCurrency(selectedRoom.personcharge)}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <AttachMoney color="success" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Total Charge" 
                                                        secondary={<Typography color="success.main" fontWeight="bold">
                                                            {formatCurrency(selectedRoom.defaultrate + selectedRoom.bedcharge + selectedRoom.personcharge)}
                                                        </Typography>}
                                                    />
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                Room Description
                                            </Typography>
                                            <Typography variant="body2" paragraph>
                                                {selectedRoom.roomdescription || 'No description available.'}
                                            </Typography>
                                            <Typography variant="subtitle2" gutterBottom color="primary">
                                                Reservation Conditions:
                                            </Typography>
                                            <Typography variant="body2">
                                                {selectedRoom.reservecondition || 'Standard reservation conditions apply.'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {hasPermission(PERMISSIONS.BRANCHES) && (
                                    <Grid item xs={12}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom color="primary">
                                                    Branch Information
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Chip
                                                        label={getCategoriesNameById(selectedRoom.branch_id, branchNamesFilterData) || 'N/A'}
                                                        color="info"
                                                        variant="outlined"
                                                    />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Room Status: {selectedRoom.status === 1 ? 'Active' : 'Inactive'}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                {/* Room Amenities (if available) */}
                                <Grid item xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                Amenities
                                            </Typography>
                                            <Grid container spacing={1}>
                                                <Grid item>
                                                    <Chip icon={<Wifi />} label="WiFi" variant="outlined" size="small" />
                                                </Grid>
                                                <Grid item>
                                                    <Chip icon={<Tv />} label="TV" variant="outlined" size="small" />
                                                </Grid>
                                                <Grid item>
                                                    <Chip icon={<AcUnit />} label="AC" variant="outlined" size="small" />
                                                </Grid>
                                                <Grid item>
                                                    <Chip icon={<Bathtub />} label="Private Bath" variant="outlined" size="small" />
                                                </Grid>
                                                <Grid item>
                                                    <Chip icon={<RoomService />} label="Room Service" variant="outlined" size="small" />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseView}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Booking Details Dialog */}
            <Dialog
                open={openBookingView}
                onClose={handleCloseBookingView}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Booking Details</b></DialogTitle>
                <DialogContent>
                    {selectedBooking && selectedRoom && (
                        <Box sx={{ p: 2 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography variant="h5" color="primary">
                                            Booking #{selectedBooking.booking_no || `BK${selectedBooking.id}`}
                                        </Typography>
                                        <Chip
                                            label={selectedBooking.status === 2 ? 'Checked In' : 'Booked'}
                                            color={selectedBooking.status === 2 ? 'error' : 'warning'}
                                            variant="filled"
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                Room Information
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                <MeetingRoom color="primary" />
                                                <Typography variant="body1" fontWeight="bold">
                                                    {selectedRoom.roomtype}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                Capacity: {selectedRoom.capacity} persons
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                Stay Duration
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                                <CalendarToday color="primary" />
                                                <Typography variant="body2">
                                                    {calculateNights(selectedBooking.check_in, selectedBooking.check_out)} night(s)
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                Check In: {formatDate(selectedBooking.check_in)}
                                            </Typography>
                                            <Typography variant="body2">
                                                Check Out: {formatDate(selectedBooking.check_out)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                Customer Details
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                <Person color="primary" />
                                                <Typography variant="body1" fontWeight="bold">
                                                    {selectedBooking.customer_name}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                Phone: {selectedBooking.customer_mobile || 'N/A'}
                                            </Typography>
                                            <Typography variant="body2">
                                                Email: {selectedBooking.customer_email || 'N/A'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                Payment Information
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Total Amount
                                                    </Typography>
                                                    <Typography variant="h6" color="primary">
                                                        {formatCurrency(selectedBooking.total_amount || 0)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Advance Paid
                                                    </Typography>
                                                    <Typography variant="h6" color="success">
                                                        {formatCurrency(selectedBooking.advance_amount || 0)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Balance Due
                                                    </Typography>
                                                    <Typography variant="h6" color="warning">
                                                        {formatCurrency((selectedBooking.total_amount || 0) - (selectedBooking.advance_amount || 0))}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Payment Mode
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {selectedBooking.payment_mode || 'N/A'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {selectedBooking.booking_remarks && (
                                    <Grid item xs={12}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom color="primary">
                                                    Remarks
                                                </Typography>
                                                <Typography variant="body2">
                                                    {selectedBooking.booking_remarks}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                {hasPermission(PERMISSIONS.BRANCHES) && (
                                    <Grid item xs={12}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom color="primary">
                                                    Branch Information
                                                </Typography>
                                                <Chip
                                                    label={getCategoriesNameById(selectedBooking.branch_id, branchNamesFilterData) || 'N/A'}
                                                    color="info"
                                                    variant="outlined"
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseBookingView}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default RoomStatus;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});